"use client";

import * as React from "react";
import { TrendingUp } from "lucide-react";
import { Pie, PieChart, Label } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "../components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
import { Skeleton } from "../components/ui/skeleton";

export function PieChartComponent({
  title,
  description,
  data,
  centerLabel = "Materials", // New prop for center label
  trendText = "Trending up by 5.2% this month",
  footerText = "Showing total material for the last 6 months",
  showTrendIcon = true,
  loading = false,
}) {
  const totalMaterials = React.useMemo(() => {
    return data.reduce((acc, curr) => acc + curr.material, 0);
  }, [data]);

  const chartConfig = data.reduce((config, item, index) => {
    const colorKey = `chart-${index + 1}`;
    config[item.materialItem] = {
      label: item.materialItem,
      color: `hsl(var(--${colorKey}))`,
    };
    return config;
  }, {});

  return (
    <Card className="flex flex-col">
      <CardHeader className="items-center text-center pb-0">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex-1 pb-0 w-full overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center aspect-square max-h-[300px]">
            <Skeleton className="h-full w-full rounded-full" />
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="mx-auto aspect-square max-h-[300px]"
          >
            <PieChart>
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Pie
                data={data}
                dataKey="material"
                nameKey="materialItem"
                innerRadius={50}
                strokeWidth={8}
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return (
                        <text
                          x={viewBox.cx}
                          y={viewBox.cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                        >
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="fill-foreground text-xl sm:text-2xl lg:text-3xl font-bold"
                          >
                            {totalMaterials.toLocaleString()}
                          </tspan>

                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-muted-foreground"
                          >
                            {centerLabel}
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
              <ChartLegend
                content={<ChartLegendContent nameKey="materialItem" />}
                className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center"
              />
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        {loading ? (
          <>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </>
        ) : (
          <>
            {trendText && (
              <div className="flex items-center gap-2 font-medium leading-none">
                {trendText}{" "}
                {showTrendIcon && <TrendingUp className="h-4 w-4" />}
              </div>
            )}
            {footerText && (
              <div className="leading-none text-muted-foreground">
                {footerText}
              </div>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  );
}
